import { useMutation } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { PasswordFields, passwordPairReady } from '../components/PasswordFields';
import { api } from '../lib/api';
import { useSession } from '../lib/session';

/**
 * The screen shown when a password still has to be changed.
 *
 * It is not a page you navigate to — it replaces the whole application while
 * the requirement stands, because the API refuses every other route anyway.
 * Rendering the console behind a dismissible prompt would produce a screen
 * where nothing works and no explanation of why.
 *
 * Deliberately no navigation and no sign-out shortcut other than the plain
 * link: the way out is to choose a password.
 */
export function ChangePasswordPage() {
  const { user, signOut } = useSession();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () => api.changePassword(current, next),
    // A full reload rather than a state update: the session's capabilities and
    // the must-change flag both come from the server, and re-fetching every
    // query by hand is how one stale copy survives.
    onSuccess: () => window.location.assign('/'),
    onError: (e) => setError((e as Error).message),
  });

  const ready =
    current.length > 0 &&
    passwordPairReady(next, confirm, {
      email: user?.email,
      name: user?.name,
      currentPassword: current,
    });

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <div className="card">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50">
            <KeyRound className="h-6 w-6 text-primary-600" aria-hidden="true" />
          </div>

          <h1 className="font-heading text-xl">Choose your own password</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            The password you signed in with was set by someone else, so two people know it. Pick one
            only you know before you carry on.
          </p>

          <form
            className="mt-5 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              change.mutate();
            }}
          >
            {error && (
              <p className="rounded-xl bg-danger-50 p-3 text-sm text-danger-700">{error}</p>
            )}

            <label className="block">
              <span className="mb-1 block text-sm font-medium">The password you were given</span>
              <input
                type="password"
                autoComplete="current-password"
                className="input w-full"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
              />
            </label>

            {/* The same component as the by-choice screen on /account. Two
                copies of these rules drifted apart the last time. */}
            <PasswordFields
              value={next}
              confirm={confirm}
              onValueChange={setNext}
              onConfirmChange={setConfirm}
              email={user?.email}
              name={user?.name}
              currentPassword={current}
            />

            <button type="submit" className="btn-primary w-full" disabled={!ready || change.isPending}>
              {change.isPending ? 'Saving…' : 'Save and continue'}
            </button>
          </form>

          <p className="mt-4 text-xs text-muted-foreground">
            Signed in as {user?.email}. Any other session you have open will be signed out.{' '}
            <button
              type="button"
              className="underline hover:text-foreground"
              onClick={() => void signOut()}
            >
              Sign out instead
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
